import { format } from 'date-fns';
import type { ArInvoiceSendEvent } from '@erp/rpc';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Num, T } from 'gt-react';

interface InvoiceSentCellProps {
  /**
   * ISO timestamp of the earliest send event, or null when the invoice has
   * never been sent.
   */
  firstSentAt: string | null;
  /**
   * Every send event for the invoice, ordered oldest → newest. The cell
   * renders the earliest date with a hover-card listing all of them — even
   * for a single send, since the note inside the card is the only place the
   * channel (email vs EDI) and recipient are surfaced.
   */
  sendEvents: ArInvoiceSendEvent[];
  /** Tailwind classes applied to the date text. */
  className?: string;
}

/**
 * Cell renderer for the "Sent" column on AR invoice tables. Shows the
 * earliest send date and opens a hover-card with every send event
 * (timestamp + integration-generated note like "Invoice emailed to
 * ap@example.com" or "Invoice sent via EDI to Hannaford") — even for a
 * single send, since the note is the only place the channel and recipient
 * appear.
 */
export function InvoiceSentCell({
  firstSentAt,
  sendEvents,
  className,
}: InvoiceSentCellProps) {
  if (!firstSentAt || sendEvents.length === 0) {
    return <span className={className}>—</span>;
  }

  const earliest = new Date(firstSentAt);
  const displayDate = format(earliest, 'MM/dd/yyyy');
  const isResend = sendEvents.length > 1;

  return (
    <HoverCard openDelay={150} closeDelay={50}>
      <HoverCardTrigger asChild>
        <span
          className={`${className ?? ''} cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2`.trim()}
        >
          {displayDate}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-80 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">
          {isResend ? (
            <T>
              Sent <Num>{sendEvents.length}</Num> times
            </T>
          ) : (
            <T>Send history</T>
          )}
        </div>
        <ul className="max-h-64 overflow-y-auto divide-y">
          {sendEvents.map((event, idx) => (
            <li
              key={`${event.occurredAt}-${idx}`}
              className="px-3 py-2 text-xs"
            >
              <div className="font-medium text-foreground">
                {format(new Date(event.occurredAt), 'MMM d, yyyy h:mm a')}
              </div>
              {event.note ? (
                <div className="text-muted-foreground mt-0.5">{event.note}</div>
              ) : null}
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}
